import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState, useCallback } from 'react'
import { PieChart, Pie, Cell, Sector, ResponsiveContainer, Tooltip } from 'recharts'

import { ChartData } from '../../types'
import { ChartContainer } from '../common/chart'
import { useIsMobile } from '../../hooks/use-mobile'
import { useStableCallback } from '../../hooks/use-stable-callback'

export const PaymentDistribution = ({
  customerCompanyId,
  paymentsPaidQuantity,
  paymentsPendingQuantity,
  paymentsReturnedQuantity,
  paymentsValidatedQuantity,
  paymentsErrorOrRejectedQuantity
}: {
  customerCompanyId: string
  paymentsPaidQuantity: number
  paymentsPendingQuantity: number
  paymentsReturnedQuantity: number
  paymentsValidatedQuantity: number
  paymentsErrorOrRejectedQuantity: number
}) => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { t } = useTranslation(['insights-funding-history-management'])

  const [activeIndex, setActiveIndex] = useState(0)
  const [data, setData] = useState<Array<ChartData>>([
    { name: t('insights.charts.paymentDistribution.conventions.paid'), value: 0, color: '#22C55E' },
    { name: t('insights.charts.paymentDistribution.conventions.pending'), value: 0, color: '#F59E0B' },
    { name: t('insights.charts.paymentDistribution.conventions.returned'), value: 0, color: '#6B30FF' },
    { name: t('insights.charts.paymentDistribution.conventions.rejected'), value: 0, color: '#EF4444' },
    { name: t('insights.charts.paymentDistribution.conventions.validated'), value: 0, color: '#0EA5E9' }
  ])

  useEffect(() => {
    const dataUpdated = [...data]

    dataUpdated[0].value = paymentsPaidQuantity
    dataUpdated[1].value = paymentsPendingQuantity
    dataUpdated[2].value = paymentsReturnedQuantity
    dataUpdated[3].value = paymentsErrorOrRejectedQuantity
    dataUpdated[4].value = paymentsValidatedQuantity

    setData(dataUpdated)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsPaidQuantity, paymentsPendingQuantity, paymentsErrorOrRejectedQuantity, paymentsReturnedQuantity])

  const config = data.reduce((acc, item) => {
    acc[item.name] = { color: item.color, label: item.name }
    return acc
  }, {} as Record<string, { color: string; label: string }>)

  const onPieEnter = useStableCallback((_: any, index: number) => {
    setActiveIndex(index)
  })

  const renderActiveShape = useCallback((props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props

    const expandedOuterRadius = outerRadius + outerRadius * 0.05

    return (
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={expandedOuterRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    )
  }, [])

  const total = data.reduce((sum, item) => sum + item.value, 0)

  const CustomTooltip = useCallback(
    ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        const percentage = ((payload[0].value / total) * 100).toFixed(1)
        const count = Math.round(payload[0].value)

        return (
          <div className="bg-white px-4 py-2 rounded-md shadow-lg border border-gray-200 text-sm">
            <p className="text-[#737373] font-medium">{t('insights.charts.paymentDistribution.tooltip', { percentage, count })}</p>
          </div>
        )
      }
      return null
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [total]
  )

  const handleViewHistory = useStableCallback(() => {
    navigate(`/customer-company-payments-history/${customerCompanyId}`)
  })

  const innerRadiusPercent = isMobile ? '70%' : '75%'
  const outerRadiusPercent = isMobile ? '90%' : '95%'

  return (
    <div
      className="border border-[#E5E7EB] bg-[#F9FAFB] flex flex-col items-stretch w-full px-3 py-4 md:px-6 md:py-6 rounded-[24px] border-solid"
      style={{ height: '400px' }}
    >
      <div className="flex justify-between items-center">
        <div
          style={{
            fontFamily: 'Open Sans',
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '24px',
            color: '#107569'
          }}
        >
          {t('insights.charts.paymentDistribution.title')}
        </div>
        <button
          onClick={handleViewHistory}
          className={`text-[#0e9384] ${isMobile ? 'text-xs' : 'text-lg'} font-semibold`}
        >
          {t('insights.charts.paymentDistribution.viewHistory')}
        </button>
      </div>
      <div className={`${isMobile ? 'h-[180px]' : 'h-[300px]'} w-full mt-4 md:mt-4`}>
        <ChartContainer
          config={config}
          className="h-full aspect-auto mt-0"
        >
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <PieChart>
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={innerRadiusPercent}
                outerRadius={outerRadiusPercent}
                fill="#8884d8"
                dataKey="value"
                onMouseEnter={onPieEnter}
                startAngle={90}
                endAngle={450}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>

      <div className="self-center flex w-full flex-wrap justify-center gap-2 md:gap-4 text-xs md:text-sm font-normal mt-2 md:mt-6">
        {data.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-1 md:gap-2"
          >
            <div
              className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} rounded-full`}
              style={{ backgroundColor: item.color }}
            />
            <div className="text-[#737373]">{item.name}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
